import './index.less'
import * as d3 from 'd3'
import { useCallback, useEffect, useState } from 'react'
import { nextBtn, prevBtn } from '@/assets/images'
import {
  setAnalysisImageId,
  setAnalysisWorkId
} from '@/features/SetAnalysisImageInfo/setAnalysisImageInfo'
import { useAppDispatch } from '@/hooks'
import { setCompareImageId } from '@/features/SetCompareImageInfo/setCompareImageInfo'
import { SRC_URL } from '@/server'
import { setShowType } from '@/features/SetCompareInfo/setCompareInfo'

interface selectWorkListProps {
  showWorkList: {
    // 作品workid
    id: string
    // 作品描述
    desc?: string
    // 画作imageId
    imageId: string
    // 画作的宽
    width?: number
    // 画作的高
    height?: number
    // 画作最大放缩level
    levelMax?: number
    // 画作最小放缩level
    levelMin?: number
    // 画作的tilesPath
    tilesPath?: string
    // 画作缩略图的src
    src: string
  }[]
  // DetailsPage，用于选择workIndex
  selectedWorkIndex?: number
  setSelectedWorkIndex?: Function
  // 展示列表的大小
  listSize: number
  // 用于跳转到画作分析页面
  setComponent?: Function
  // 展示类型 details(0)/画作选择(1)/画作对比(2)
  type: number
}
// 待选画作的展示列表组件
export function SelectWorkList(props: selectWorkListProps) {
  const dispatch = useAppDispatch()
  const { showWorkList, selectedWorkIndex, listSize, type } = props
  /**
   * 由selectedWorkIndex计算firstIndex
   * @params selectedId: 选中画作的index
   * @params size: 列表大小
   * @return firstIndex: 选中当前画作列表的第一幅画index
   */
  const getFirstIndexBySelectedIndex = useCallback((selectedId: number, size: number) => {
    if (selectedId) return size * ~~(selectedId / size)
    else return 0
  }, [])
  // 选择列表中第一幅画的index
  const [firstIndex, setFirstIndex] = useState<number>(
    getFirstIndexBySelectedIndex(props.selectedWorkIndex ?? 0, listSize)
  )
  /**
   * 翻页的回调
   * @param index: 以index为第一幅画，展示画作列表
   */
  const handlePageTurning = function (index: number) {
    if (index < 0) index = 0
    if (index >= showWorkList.length) index = showWorkList.length - 1
    // 获取展示列表div
    const div = d3.select('#painting-select-list')
    // 获取自适应大小后，画作元素的width、borderWidth
    const width = div._groups[0][0].children[0].clientWidth
    const borderWidth =
      2 *
      Number(
        getComputedStyle(div._groups[0][0].children[0].children[0], null)[
          'border-left-width'
        ].slice(0, -2)
      )
    // 每张画画作之间的间隔 offset
    const offset = (width - borderWidth) * index
    // 滑动过度效果
    div.style('transform', 'translate(-' + offset + 'px,0px)').style('transition-duration', '.5s')
    // 修改展示列表第一幅画index
    setFirstIndex(index)
  }
  /**
   * 选择画作的回调
   * @param index：展示列表的画作索引
   */
  const handleSelectWrok = function (index: number) {
    // 详情页面(DetailsPage)
    if (props.setSelectedWorkIndex) props.setSelectedWorkIndex(index)
    // 画作选择页面
    if (type === 1) {
      // 分析页面中的workid
      dispatch(setAnalysisWorkId(showWorkList[index].id))
      // 分析页面中的imageid
      dispatch(setAnalysisImageId(showWorkList[index].imageId))
      // 跳转到分析页面
      if (props.setComponent) props.setComponent(2)
    }
    //画作对比页面
    else if (type === 2) {
      // 对比页面中的imageid
      dispatch(setCompareImageId(showWorkList[index].imageId))
      dispatch(setShowType(2))
    }
  }
  // 列表内容改变，div移动到指定位置
  useEffect(() => {
    const nowFirstIndex = getFirstIndexBySelectedIndex(props.selectedWorkIndex ?? 0, listSize)
    // 获取展示列表div
    const div = d3.select('#painting-select-list')
    // 获取自适应大小后，画作元素的width、borderWidth
    const width = div._groups[0][0].children[0]?.clientWidth ?? 0
    const borderWidth =
      2 *
      Number(
        getComputedStyle(div._groups[0][0]?.children[0]?.children[0] ?? div._groups[0][0], null)[
          'border-left-width'
        ].slice(0, -2)
      )
    // 每张画画作之间的间隔 offset
    const offset = (width - borderWidth) * nowFirstIndex
    div.style('transform', 'translate(-' + offset + 'px,0px)').style('transition-duration', '.5s')
    setFirstIndex(nowFirstIndex)
  }, [showWorkList, getFirstIndexBySelectedIndex, props.selectedWorkIndex, listSize])

  /**
   * 计算图片应有宽度
   */
  const calcImgWidth = function () {
    try {
      const listDiv = d3.select('#painting-list')
      const listWidth = listDiv._groups[0][0].clientWidth
      const itemDiv = d3.select('#painting-select-list')
      const children = itemDiv._groups[0][0].children
      const newWidth = listWidth / (listSize + 2)
      for (const element in children) {
        if (typeof children[element].children === 'object') {
          children[element].children[0].children[0].style.cssText = 'width:' + newWidth + 'px'
        }
      }
    } catch (err) {
      console.log(err)
    }
  }
  useEffect(() => {
    calcImgWidth()
  })
  //浏览器窗口大小改变时，重新计算图片自适应大小
  useEffect(() => {
    window.onresize = calcImgWidth
    return () => {
      window.removeEventListener('resize', calcImgWidth)
    }
  })
  return (
    <>
      <div
        className='painting-select-btn'
        onClick={() => {
          handlePageTurning(firstIndex - listSize)
        }}
      >
        <img src={prevBtn} alt='上一页' />
      </div>
      <div className='painting-list' id='painting-list'>
        <div className='painting-select-list' id='painting-select-list'>
          {showWorkList.map((item, index) => {
            return (
              <div className='painting-select-item' onClick={() => handleSelectWrok(index)}>
                <div
                  style={{
                    border: index === selectedWorkIndex ? '3px solid red' : 'none'
                  }}
                >
                  <img src={SRC_URL + item.src} alt='待选画' />
                </div>
                <div className='select-text'>
                  <div className='select-index'>{index + 1}</div>
                </div>
              </div>
            )
          })}
        </div>
        <div className='total-text'>共{showWorkList.length}幅 </div>
      </div>
      <div
        className='painting-select-btn'
        onClick={() => {
          handlePageTurning(firstIndex + listSize)
        }}
      >
        <img src={nextBtn} alt='下一页' />
      </div>
    </>
  )
}
